<template>
    <div>
        <template v-if="resetErr">
            <EmptyPage 
                state="error_custom" 
                btn-state="custom"
                title="Invalid or expired token" 
                subtitle="An error occurred while resetting your password"
            >
                <template #button>
                    <div class="flex align-items-center flex-col justify-content-center">
                        <BaseButton 
                            label="Back to reset password page" 
                            class="text--sm" 
                            @submit="goBackToLoginPage"
                        />
                    </div>
                </template> 
            </EmptyPage>
        </template>
        <template v-else>
            <template v-if="!isResetSuccessful">
                <AuthForm 
                    auth-type="reset" 
                    show-form-img
                    :loading="resetLoading" 
                    :disabled="resetLoading" 
                    @form-submit="resetPassword"
                >
                    <template #icon>
                        <AuthResetIcon />
                    </template>
                </AuthForm>
            </template>

            <template v-else>
                <div class="auth__form__wrap">
                    <div class="follow__up__preview">
                        <p class="text--semi--bold text-gray-400 mb--10 text--md">
                            Password reset successful!
                        </p>

                        <p class="text--sm mb--10 text-gray-400">
                            You have successfully reset your password. Click the button below to go to your dashboard.
                        </p> 

                        <div class="mt--15 flex align-items-center">
                            <BaseButton 
                                label="Go to dashboard" 
                                @submit="goToDashboard"  
                            />
                        </div>
                    </div>
                </div>
            </template>
        </template>
    </div>
</template>

<script>
import { EmptyPage, BaseButton } from "@/components/ui";
import AuthForm from "@/components/auth/AuthForm.vue";
import { validatePassword } from "@/utils/helpers";
import AuthResetIcon from "@/components/ui/icons/AuthResetIcon.vue";

export default {
    name: "ResetPage",

    components: {
        AuthForm, 
        EmptyPage, 
        BaseButton, AuthResetIcon,
    },

    beforeRouteEnter(to, from, next) {        
        next(async(vm) => {            
            if (to.name === "reset") {    
                const params = new URLSearchParams(window.location.search);
                const email = params.get("email");
                const token = params.get("token");        

                if (!email || !token) {
                    return vm.$router.push("/auth/login");
                }

                vm.email = email;
                vm.token = token;
            }
        });
    },

    data() {
        return {
            resetLoading: false,
            resetErr: false,
            resetErrMsg: "",
            email: "",
            token: "",
            passwordValidErrMsg: "",
            isResetSuccessful: false,
        };
    },

    methods: {
        goBackToLoginPage() {
            this.$router.push("/auth/initiate-reset");
        },

        goToDashboard() {
            this.$store.dispatch("auth/setAuthStatus", true);
            this.$store.dispatch("auth/setLastChecked", Date.now());
            this.$toast.success("Reset password successful");
            this.$router.push({ name: "reports", query: { type: "clients", range: "1_year" } });
        },

        async validatePasswords(values) {
            if (values.password !== values.confirmPassword) {
                return "Passwords do not match";
            }

            const isPasswordValid = validatePassword(values.password);
            
            if (!isPasswordValid) {
                return "Invalid password format";
            }

            return "";
        },

        async resetPassword(data) {
            try {
                this.resetError = false;
                this.resetErrMsg = "";
                this.isResetSuccessful = false;
                this.resetLoading = true;

                const isPasswordsValid = this.validatePasswords({ 
                    password: data?.password,
                    confirmPassword: data?.confirmPassword,
                });

                if (!isPasswordsValid) {
                    this.$toast.error(isPasswordsValid);
                    return;
                }
                                
                await this.$store.dispatch("auth/resetPassword", { 
                    email: this.email || "",
                    newPassword: data?.password,
                    confirmNewPassword: data?.confirmPassword,
                    token: this.token || "",
                });

                this.isResetSuccessful = true;
                this.email = "";
                this.token = "";
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;

                if (errMsg.includes("Previously used passwords are not allowed")) {
                    this.$toast.error("Previously used passwords are not allowed. Try a different password.");
                } else {
                    this.resetErr = true;
                    this.resetErrMsg = errMsg || "An error occurred while resetting your password";
                }
            } finally {
                this.resetLoading = false;
            };
        },
    },
};
</script>